/**
 * FaceCapture Component
 *
 * Camera-based face capture with detection overlay and guidance.
 * Handles camera permissions, face detection, and descriptor extraction.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';
import { useFaceDetection, useCameraStream } from '../hooks/useFaceDetection';
import type { FaceCaptureProps, FaceDetectionResult } from '../types';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MULTI_SAMPLE_COUNT = 5; // Number of samples for enrollment
const DEFAULT_AUTH_SAMPLE_COUNT = 3; // Number of samples for authentication
const SAMPLE_INTERVAL_MS = 500; // Time between samples

// ============================================================================
// Component
// ============================================================================

export const FaceCapture: React.FC<FaceCaptureProps> = ({
  onCapture,
  onError,
  autoCapture = false,
  showGuide = true,
  enableLiveness = false,
  multiSampleCount = 1, // Number of samples to capture and average
  onMultiCapture, // Callback for multi-sample capture (receives array of descriptors)
}) => {
  console.log('[ZK FaceCapture] Component rendering, multiSampleCount:', multiSampleCount);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Camera hook
  const { stream, error: cameraError, isLoading: cameraLoading, startCamera, stopCamera } = useCameraStream();

  // Face detection hook
  const {
    isModelLoaded,
    isLoading: modelLoading,
    isDetecting,
    detectedFace,
    error: detectionError,
    startDetection,
    stopDetection,
    captureDescriptor,
  } = useFaceDetection();

  // Local state
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [captureCountdown, setCaptureCountdown] = useState<number | null>(null);
  const [capturedSamples, setCapturedSamples] = useState<Float32Array[]>([]);
  const [isCapturingSamples, setIsCapturingSamples] = useState(false);

  // Debug logging for state changes
  console.log('[ZK FaceCapture] State:', {
    stream: !!stream,
    cameraLoading,
    cameraError,
    isModelLoaded,
    modelLoading,
    isDetecting,
    detectedFace: !!detectedFace,
    isCameraReady,
  });

  // Colors
  const bgColor = useColorModeValue('gray.100', 'gray.800');
  const guideColor = useColorModeValue('blue.400', 'blue.300');
  const detectedColor = useColorModeValue('green.400', 'green.300');
  const warningColor = useColorModeValue('orange.400', 'orange.300');

  // ============================================================================
  // Effects
  // ============================================================================

  // Start camera on mount
  useEffect(() => {
    console.log('[ZK FaceCapture] Mount effect - starting camera');
    startCamera();

    return () => {
      console.log('[ZK FaceCapture] Cleanup - stopping camera and detection');
      stopCamera();
      stopDetection();
    };
  }, []);

  // Attach stream to video element
  useEffect(() => {
    console.log('[ZK FaceCapture] Stream effect - stream:', !!stream, 'videoRef:', !!videoRef.current);
    if (videoRef.current && stream) {
      console.log('[ZK FaceCapture] Attaching stream to video element');
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        console.log('[ZK FaceCapture] Video metadata loaded, playing video');
        console.log('[ZK FaceCapture] Video dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
        videoRef.current?.play();
        setIsCameraReady(true);
        console.log('[ZK FaceCapture] Camera ready set to true');
      };
    }
  }, [stream]);

  // Start detection when camera is ready (models will be loaded by startDetection)
  useEffect(() => {
    console.log('[ZK FaceCapture] Detection trigger effect - isCameraReady:', isCameraReady, 'isModelLoaded:', isModelLoaded, 'videoRef:', !!videoRef.current);
    // Note: We start detection when camera is ready - startDetection will load models if needed
    if (isCameraReady && videoRef.current) {
      console.log('[ZK FaceCapture] Camera ready - starting face detection (models will load if needed)!');
      startDetection(videoRef.current);
    } else {
      console.log('[ZK FaceCapture] Waiting for:', {
        needsCameraReady: !isCameraReady,
        needsVideoRef: !videoRef.current,
      });
    }
  }, [isCameraReady, startDetection]);

  // Draw detection overlay
  useEffect(() => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match canvas size to video
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw face guide (oval)
    if (showGuide) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radiusX = canvas.width * 0.25;
      const radiusY = canvas.height * 0.35;

      ctx.strokeStyle = detectedFace ? detectedColor : guideColor;
      ctx.lineWidth = 3;
      ctx.setLineDash(detectedFace ? [] : [10, 10]);

      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw bounding box if face detected
    if (detectedFace) {
      const { boundingBox } = detectedFace;

      ctx.strokeStyle = detectedColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([]);

      ctx.strokeRect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height);

      // Draw confidence
      ctx.fillStyle = detectedColor;
      ctx.font = '14px sans-serif';
      ctx.fillText(
        `Confidence: ${(detectedFace.confidence * 100).toFixed(0)}%`,
        boundingBox.x,
        boundingBox.y - 5
      );
    }
  }, [detectedFace, showGuide, guideColor, detectedColor]);

  // Handle errors
  useEffect(() => {
    if (cameraError) {
      onError(cameraError);
    }
    if (detectionError) {
      onError(detectionError);
    }
  }, [cameraError, detectionError, onError]);

  // ============================================================================
  // Handlers (defined before effects that use them)
  // ============================================================================

  // Helper function to average descriptors
  const averageDescriptors = useCallback((descriptors: Float32Array[]): Float32Array => {
    const length = descriptors[0].length;
    const result = new Float32Array(length);

    for (let i = 0; i < length; i++) {
      let sum = 0;
      for (const descriptor of descriptors) {
        sum += descriptor[i];
      }
      result[i] = sum / descriptors.length;
    }

    return result;
  }, []);

  const handleCapture = useCallback(async () => {
    console.log('[ZK FaceCapture] handleCapture called - capturing descriptor...');

    // Single sample mode
    if (multiSampleCount <= 1) {
      const descriptor = await captureDescriptor();

      if (descriptor) {
        console.log('[ZK FaceCapture] Descriptor captured successfully! Length:', descriptor.length);
        onCapture(descriptor);
      } else {
        console.log('[ZK FaceCapture] Failed to capture descriptor');
        onError('Failed to capture face. Please try again.');
      }

      setCaptureCountdown(null);
      return;
    }

    // Multi-sample mode - capture multiple samples and average them
    console.log('[ZK FaceCapture] Starting multi-sample capture, need', multiSampleCount, 'samples');
    setIsCapturingSamples(true);
    setCapturedSamples([]);

    const samples: Float32Array[] = [];

    for (let i = 0; i < multiSampleCount; i++) {
      console.log('[ZK FaceCapture] Capturing sample', i + 1, 'of', multiSampleCount);

      // Wait a bit between samples for slight face variation
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, SAMPLE_INTERVAL_MS));
      }

      const descriptor = await captureDescriptor();

      if (descriptor) {
        samples.push(descriptor);
        setCapturedSamples([...samples]);
        console.log('[ZK FaceCapture] Sample', i + 1, 'captured successfully');
      } else {
        console.log('[ZK FaceCapture] Failed to capture sample', i + 1);
      }
    }

    setIsCapturingSamples(false);
    setCaptureCountdown(null);

    if (samples.length < Math.ceil(multiSampleCount / 2)) {
      console.log('[ZK FaceCapture] Too few samples captured:', samples.length);
      onError('Failed to capture enough face samples. Please try again.');
      return;
    }

    console.log('[ZK FaceCapture] Multi-sample capture complete!', samples.length, 'samples');

    // Average the samples
    const averaged = averageDescriptors(samples);
    console.log('[ZK FaceCapture] Averaged descriptor created, length:', averaged.length);

    // Call the appropriate callback
    if (onMultiCapture) {
      onMultiCapture(samples);
    }
    onCapture(averaged);
  }, [captureDescriptor, onCapture, onMultiCapture, onError, multiSampleCount, averageDescriptors]);

  // ============================================================================
  // Capture-related Effects
  // ============================================================================

  // Auto-capture when face is stable
  useEffect(() => {
    if (autoCapture && detectedFace && detectedFace.confidence > 0.7) {
      console.log('[ZK FaceCapture] Face detected with high confidence:', detectedFace.confidence.toFixed(2));
      // Start countdown if not already started
      if (captureCountdown === null) {
        console.log('[ZK FaceCapture] Starting capture countdown!');
        setCaptureCountdown(3);
      }
    } else if (autoCapture && detectedFace) {
      console.log('[ZK FaceCapture] Face detected but confidence too low:', detectedFace?.confidence?.toFixed(2));
      setCaptureCountdown(null);
    } else {
      // Reset countdown if face lost
      if (captureCountdown !== null) {
        console.log('[ZK FaceCapture] Face lost, resetting countdown');
      }
      setCaptureCountdown(null);
    }
  }, [autoCapture, detectedFace, captureCountdown]);

  // Countdown timer
  useEffect(() => {
    if (captureCountdown === null) return;

    if (captureCountdown === 0) {
      handleCapture();
      return;
    }

    const timer = setTimeout(() => {
      setCaptureCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [captureCountdown, handleCapture]);

  // ============================================================================
  // Render
  // ============================================================================

  const isLoading = cameraLoading || modelLoading;
  const error = cameraError || detectionError;

  return (
    <VStack spacing={4} align="stretch">
      {/* Camera View */}
      <Box
        position="relative"
        bg={bgColor}
        borderRadius="lg"
        overflow="hidden"
        aspectRatio={4 / 3}
      >
        {/* Video Element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)', // Mirror for selfie view
          }}
        />

        {/* Detection Overlay */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            transform: 'scaleX(-1)', // Mirror to match video
            pointerEvents: 'none',
          }}
        />

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="blackAlpha.600"
          >
            <VStack spacing={3}>
              <Spinner size="xl" color="white" />
              <Text color="white" fontSize="sm">
                {cameraLoading ? 'Starting camera...' : 'Loading face detection...'}
              </Text>
            </VStack>
          </Box>
        )}

        {/* Countdown Overlay */}
        {captureCountdown !== null && captureCountdown > 0 && !isCapturingSamples && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="blackAlpha.400"
          >
            <Text fontSize="8xl" fontWeight="bold" color="white">
              {captureCountdown}
            </Text>
          </Box>
        )}

        {/* Multi-sample Progress Overlay */}
        {isCapturingSamples && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="blackAlpha.500"
          >
            <VStack spacing={3}>
              <Spinner size="xl" color="white" />
              <Text fontSize="xl" fontWeight="bold" color="white">
                Capturing {capturedSamples.length + 1} of {multiSampleCount}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800">
                Hold still...
              </Text>
            </VStack>
          </Box>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Status and Instructions */}
      <Box textAlign="center">
        {!isLoading && !error && (
          <Text fontSize="sm" color={detectedFace ? 'green.500' : 'gray.500'}>
            {detectedFace
              ? 'Face detected! Hold still...'
              : 'Position your face in the oval guide'}
          </Text>
        )}
      </Box>

      {/* Capture Button (manual mode) */}
      {!autoCapture && (
        <Button
          colorScheme="blue"
          size="lg"
          onClick={handleCapture}
          isDisabled={!detectedFace || isLoading}
        >
          {detectedFace ? 'Capture Face' : 'No Face Detected'}
        </Button>
      )}
    </VStack>
  );
};

export default FaceCapture;
